import React from 'react';
import { Drawer, Button, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import { CloseOutlined } from '@ant-design/icons';

/**
 * StandardDrawer 组件属性
 */
interface StandardDrawerProps {
  /** 是否显示 */
  visible?: boolean;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认回调 */
  onOk?: () => void;
  /** 标题 */
  title?: string;
  /** 内容区域 */
  children?: React.ReactNode;
  /** 确认按钮 loading 状态 */
  confirmLoading?: boolean;
  /** 确认按钮文本 */
  okText?: string;
  /** 取消按钮文本 */
  cancelText?: string;
  /** 抽屉宽度 (480/720/960) */
  width?: number;
  /** 是否显示底部操作栏 */
  showFooter?: boolean;
  /** 点击遮罩是否可关闭 */
  maskClosable?: boolean;
  /** 关闭时是否销毁 DOM */
  destroyOnClose?: boolean;
  /** 确认按钮是否禁用 */
  okDisabled?: boolean;
}

/**
 * 标准 Drawer 组件 - UI/UX 规范
 *
 * 规范要求：
 * - 宽度：简单表单 480px，中等复杂度 720px，复杂表单 960px
 * - 高度：100vh 全屏
 * - 位置：从右侧滑出
 * - 底部操作栏：固定在 Drawer 底部
 * - 关闭方式：右上角 X、ESC 键、点击遮罩层
 */
const StandardDrawer: React.FC<StandardDrawerProps> = ({
  visible = false,
  onClose,
  onOk,
  title,
  children,
  confirmLoading = false,
  okText,
  cancelText,
  width = 480,
  showFooter = true,
  maskClosable = true,
  destroyOnClose = true,
  okDisabled = false,
  ...restProps
}) => {
  const { t } = useTranslation();

  // 默认按钮文本
  const finalOkText = okText || t('common.save');
  const finalCancelText = cancelText || t('common.cancel');

  // 标题栏自定义
  const titleContent = (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingRight: '24px'
    }}>
      <span style={{ fontSize: '16px', fontWeight: 'bold' }}>
        {title}
      </span>
    </div>
  );

  // 底部操作栏
  const footer = showFooter ? (
    <div style={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      borderTop: '1px solid #f0f0f0',
      padding: '16px 24px',
      background: '#fff',
      textAlign: 'right',
    }}>
      <Space>
        <Button onClick={onClose}>
          {finalCancelText}
        </Button>
        {onOk && (
          <Button
            type="primary"
            onClick={onOk}
            loading={confirmLoading}
            disabled={okDisabled}
          >
            {finalOkText}
          </Button>
        )}
      </Space>
    </div>
  ) : null;

  return (
    <Drawer
      title={titleContent}
      placement="right"
      closable={true}
      closeIcon={<CloseOutlined />}
      onClose={onClose}
      open={visible}
      width={width}
      height="100vh"
      maskClosable={maskClosable}
      destroyOnClose={destroyOnClose}
      footer={footer}
      styles={{
        body: {
          paddingBottom: showFooter ? '80px' : '24px',
          height: '100%',
          overflow: 'auto',
        },
      }}
      {...restProps}
    >
      {children}
    </Drawer>
  );
};

export default StandardDrawer;
